import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { 
  ArrowLeft, 
  Calendar, 
  User, 
  Printer, 
  FileText, 
  Download,
  MessageSquare,
  Send
} from 'lucide-react';

interface Complaint {
  id: string;
  title: string;
  description: string;
  machine: string;
  currentCounter: number;
  agent: string;
  status: 'nouveau' | 'en_cours' | 'resolue';
  createdAt: string;
  updatedAt: string;
  attachments?: string[];
}

interface Message {
  id: string;
  author: string;
  authorType: 'client' | 'agent';
  message: string;
  timestamp: string;
}

interface ComplaintDetailProps {
  complaint: Complaint;
  onBack?: () => void;
  onStatusChange?: (status: Complaint['status']) => void;
}

export const ComplaintDetail = ({ complaint, onBack, onStatusChange }: ComplaintDetailProps) => {
  const { toast } = useToast();
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      author: 'Client',
      authorType: 'client',
      message: complaint.description,
      timestamp: complaint.createdAt + 'T10:30:00'
    },
    {
      id: '2',
      author: complaint.agent,
      authorType: 'agent',
      message: 'Merci pour votre réclamation. Nous examinons le problème et vous recontacterons sous 24h.',
      timestamp: complaint.createdAt + 'T14:45:00'
    }
  ]);

  const getStatusVariant = (status: Complaint['status']) => {
    switch (status) {
      case 'nouveau':
        return 'default';
      case 'en_cours':
        return 'secondary';
      case 'resolue':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: Complaint['status']) => {
    switch (status) {
      case 'nouveau':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
      case 'en_cours':
        return 'bg-orange-100 text-orange-800 hover:bg-orange-200';
      case 'resolue':
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  const getStatusLabel = (status: Complaint['status']) => {
    switch (status) {
      case 'nouveau':
        return 'Nouveau';
      case 'en_cours':
        return 'En cours de traitement';
      case 'resolue':
        return 'Résolue';
      default:
        return status;
    }
  };

  const handleSendMessage = () => {
    if (!newMessage.trim()) {
      return;
    }

    const message: Message = {
      id: Date.now().toString(),
      author: 'Client',
      authorType: 'client',
      message: newMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, message]);
    setNewMessage('');

    toast({
      title: "Message envoyé",
      description: "Votre message a été envoyé à l'agent."
    });
  };

  const handleDownloadAttachment = (filename: string) => {
    // Simulate file download
    toast({
      title: "Téléchargement",
      description: `Téléchargement de ${filename} en cours...`
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            {onBack && (
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div>
              <CardTitle className="flex items-center gap-2">
                Réclamation {complaint.id}
                <Badge 
                  variant={getStatusVariant(complaint.status)}
                  className={getStatusColor(complaint.status)}
                >
                  {getStatusLabel(complaint.status)}
                </Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">{complaint.title}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Créée le
              </div>
              <p className="font-medium">{formatDate(complaint.createdAt + 'T10:30:00')}</p>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-4 w-4" />
                Agent assigné
              </div>
              <p className="font-medium">{complaint.agent}</p>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Printer className="h-4 w-4" />
                Machine
              </div>
              <p className="font-medium">{complaint.machine}</p>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                Compteur
              </div>
              <p className="font-medium">{complaint.currentCounter.toLocaleString()}</p>
            </div>
          </div>

          {/* Attachments */}
          {complaint.attachments && complaint.attachments.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium mb-2">Pièces jointes</h4>
              <div className="flex flex-wrap gap-2">
                {complaint.attachments.map((attachment, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadAttachment(attachment)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {attachment}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Messages */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Échanges
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 mb-6">
            {messages.map((message) => (
              <div key={message.id} className="flex gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage src="" />
                  <AvatarFallback className="text-xs">
                    {getInitials(message.author)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{message.author}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(message.timestamp)}
                    </span>
                    <Badge variant={message.authorType === 'client' ? 'secondary' : 'default'} className="text-xs">
                      {message.authorType === 'client' ? 'Client' : 'Agent'}
                    </Badge>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-sm">
                    {message.message}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Separator className="my-4" />

          {/* New Message Form */}
          <div className="space-y-3">
            <Label htmlFor="new-message">Nouveau message</Label>
            <Textarea
              id="new-message"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Tapez votre message..."
              rows={3}
            />
            <div className="flex justify-end">
              <Button onClick={handleSendMessage} disabled={!newMessage.trim()}>
                <Send className="h-4 w-4 mr-2" />
                Envoyer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComplaintDetail;